import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * 헤더 액션 슬롯 — 페이지의 `Toolbar`(조회조건·다운로드 등 페이지 액션)를 **AppHeader 타이틀 줄 trailing** 으로
 * 올린다(web/ 근간: a영역 = 헤더 trailing, admin 은 본문). iOS large-title 패턴 — 타이틀+액션이 헤더 한 몸.
 * 셸(`AppShell`)이 `AppHeader` + `Outlet` 을 이 Provider 로 감싸고, `AppHeader` 가 슬롯 DOM 을 등록,
 * 페이지는 `<HeaderActions>` 로 그 슬롯에 포탈한다(React 트리 컨텍스트는 포탈로도 유지 — 핸들러·상태 정상).
 *
 * ⚠️ Directive: 슬롯은 **단일**(한 시점 한 페이지만 mount 전제 — 현재 `<Outlet/>` 은 동기 swap). 향후 native
 * 애니메이티드 아웃렛(AnimatePresence·useOutlet 스냅샷)으로 push/pop 중 **퇴장+진입 페이지가 동시 mount** 되면 두
 * `HeaderActions` 가 같은 슬롯에 겹쳐 포탈됨. 그 도입 시 slot 을 id 기반 last-writer-wins 스택으로 승격하거나
 * exiting 페이지의 액션을 억제할 것.
 */
interface HeaderActionsContextValue {
  /** 포탈 대상(AppHeader trailing div). 미마운트 시 null. */
  slot: HTMLElement | null;
  /** AppHeader 가 자신의 slot div 를 ref 콜백으로 등록. */
  setSlot: (el: HTMLElement | null) => void;
}

const HeaderActionsContext = createContext<HeaderActionsContextValue | null>(null);

export function HeaderActionsProvider({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const value = useMemo(() => ({ slot, setSlot }), [slot]);
  return <HeaderActionsContext.Provider value={value}>{children}</HeaderActionsContext.Provider>;
}

/** AppHeader 전용 — trailing 슬롯 div 를 등록하는 ref 콜백. `<div ref={useHeaderSlotRef()} />`. */
export function useHeaderSlotRef(): (el: HTMLElement | null) => void {
  return useContext(HeaderActionsContext)?.setSlot ?? (() => {});
}

/**
 * 페이지 액션을 헤더 trailing 슬롯에 렌더 — `<HeaderActions><Toolbar …/></HeaderActions>`. 슬롯 미준비면
 * 렌더 안 함(안전). Provider 밖(예: 인증 전 화면)에서도 null 로 안전. 언마운트 시 포탈 자동 정리.
 */
export function HeaderActions({ children }: { children: ReactNode }) {
  const slot = useContext(HeaderActionsContext)?.slot ?? null;
  if (!slot) return null;
  return createPortal(children, slot);
}
